import React from "react"
import { useTranslation } from "react-i18next"

import KfsPill from "./KfsPill"
import { thousandSeparator } from "utils"
import { Button, Checkbox, LazyImage } from "components"
import classes from "./LfsSpecification.module.scss"

const LfsSpecification = props => {
  const {
    specification,
    specificationItem,
    maxSelect,
    selectedSpecificationItems,
    setSelectedSpecificationItems,
    builderStepKey,
    portion = "whole"
  } = props

  const [t] = useTranslation()

  const getEffectiveMaxQuantity = () => {
    if (Number(maxSelect) > 0) return Number(maxSelect)

    if (specification.maxQuantity === 0) return undefined

    return specification.maxQuantity
  }

  const getSelectedSpecificationQuantity = (items = selectedSpecificationItems) =>
    items.reduce((sum, selectedSpecificationItem) => {
      if (specification._id !== selectedSpecificationItem.specificationId ||
          selectedSpecificationItem.builderStepKey !== builderStepKey) return sum

      return sum + (selectedSpecificationItem.quantity || 1)
    }, 0)

  const canAddSpecificationItem = () => {
    const maxQuantity =
      getEffectiveMaxQuantity()

    if (maxQuantity === undefined) return true

    return getSelectedSpecificationQuantity() < maxQuantity
  }

  const setSelectedSpecificationItemsFreeQuantity = (newSelectedSpecificationItems) => {
    let remainingFreeQuantity =
      specification.maxFreeQuantity

    const result =
      newSelectedSpecificationItems.map(selectedSpecificationItem => {
        if (selectedSpecificationItem.specificationId !== specification._id ||
            selectedSpecificationItem.builderStepKey !== builderStepKey) {
          return selectedSpecificationItem
        }

        const freeQuantity =
          Math.min(remainingFreeQuantity, selectedSpecificationItem.quantity)

        remainingFreeQuantity -= freeQuantity

        return {
          ...selectedSpecificationItem,
          freeQuantity
        }
      })


    return result
  }

  const handleRemoveItem = () => {
    setSelectedSpecificationItems(prev => {
      const newSelectedSpecificationItems =
        prev.filter(si =>
          si._id !== specificationItem._id || si.builderStepKey !== builderStepKey
        )

      const selectedSpecificationItemWithFreeQuantity =
        setSelectedSpecificationItemsFreeQuantity(
          newSelectedSpecificationItems
        )

      return selectedSpecificationItemWithFreeQuantity
    })
  }

  const createSelectedSpecificationItem = () => ({
    specificationId: specification._id,
    specificationMaxFreeQuantity: specification.maxFreeQuantity,
    ...specificationItem,
    quantity: 1,
    freeQuantity: 0,
    builderStepKey,
    portion
  })

  const handleAddSelectableItem = () => {
    if (!canAddSpecificationItem()) {
      setSelectedSpecificationItems(prev => {
        const itemIdForRemove =
          prev.find(nssi =>
            nssi.specificationId === specification._id && nssi.builderStepKey === builderStepKey
          )?._id

        const newSelectedSpecificationItems =
          [...prev, createSelectedSpecificationItem()]
            .filter(ssi => ssi._id !== itemIdForRemove || ssi.builderStepKey !== builderStepKey)

        return setSelectedSpecificationItemsFreeQuantity(
          newSelectedSpecificationItems
        )
      })

      return
    }

    setSelectedSpecificationItems(prev => {
      const newSelectedSpecificationItems =
        [...prev, createSelectedSpecificationItem()]

      return setSelectedSpecificationItemsFreeQuantity(
        newSelectedSpecificationItems
      )
    })
  }

  const handleAddItem = () => {
    if (!canAddSpecificationItem()) return

    setSelectedSpecificationItems(prev => {
      const newSelectedSpecificationItems =
        [...prev, createSelectedSpecificationItem()]

      return setSelectedSpecificationItemsFreeQuantity(
        newSelectedSpecificationItems
      )
    })
  }

  const handleChangeItem = value => {
    setSelectedSpecificationItems(prev => {
      const maxQuantity =
        getEffectiveMaxQuantity()

      const newSelectedSpecificationItems =
        prev.map(si =>
          si._id === specificationItem._id && si.builderStepKey === builderStepKey
            ? { ...si, quantity: value }
            : si
        )

      if (
        maxQuantity !== undefined &&
        getSelectedSpecificationQuantity(newSelectedSpecificationItems) > maxQuantity
      ) {
        return prev
      }

      return setSelectedSpecificationItemsFreeQuantity(
        newSelectedSpecificationItems
      )
    })
  }

  const selectedSpecificationItem =
    selectedSpecificationItems.find(si =>
      si._id === specificationItem._id && si.builderStepKey === builderStepKey
    )

  const disabled =
    !specificationItem.isAvailable ||
    !canAddSpecificationItem()

  const effectiveMaxQuantity =
    getEffectiveMaxQuantity()

  const selectedSpecificationQuantity =
    getSelectedSpecificationQuantity()

  const remainingQuantity =
    effectiveMaxQuantity === undefined
      ? undefined
      : Math.max(effectiveMaxQuantity - selectedSpecificationQuantity, 0)

  const selectedItemMaxQuantity =
    effectiveMaxQuantity === undefined
      ? undefined
      : (selectedSpecificationItem?.quantity || 0) + remainingQuantity

  return (
    <div className={classes["option"]}>
      <div className={classes["right"]}>
        <LazyImage  
          src={specificationItem.image}
          className={classes["image"]}
          alt={specificationItem.name}
          imageWidth={100}
          quality={80}
        />

        <div className={classes["option-details"]}>
          <span className={classes["name"]}>
            {specificationItem.name}
          </span>

          {
            specificationItem.salePrice > 0 &&
            <span className={classes["price"]}>
              {thousandSeparator(specificationItem.salePrice)}
            </span>
          }
        </div>
      </div>

      {specificationItem.isSelectable
        ?
        <Checkbox
          onChange={(checked) => checked ? handleAddSelectableItem() : handleRemoveItem()}
          checked={!!selectedSpecificationItem}
        />
        :
        selectedSpecificationItem
          ?
          <KfsPill
            max={selectedItemMaxQuantity}
            onChange={handleChangeItem}
            onDelete={handleRemoveItem}
            value={selectedSpecificationItem.quantity}
            className={classes["pill"]}
            min={1}
            numberType="int"
          />
          :
          <Button
            disabled={disabled}
            className={classes["button"]}
            onClick={handleAddItem}
          >
            {t("افزودن")}
          </Button>
      }
    </div>
  )
}

export default LfsSpecification
